import FieldModel from './FieldModel.js';

const input_defaults = {
	schema: null,
	table: null,
	display_name: null,
	options: {},
	fields: [],
	idx: 0,
};

class FieldsTableModel
{
	constructor(values)
	{
		this.base_object = null;

		this.fields_table_uuid = ko.observable();
		this.schema = ko.observable();
		this.table = ko.observable();
		this.display_name = ko.observable();
		this.idx = ko.observable();
		this.options = ko.observable();
		this.fields = ko.observableArray();

		this.set(values);
	}

	async fetch(table_uuid)
	{
		const requested_id = table_uuid ?? this.fields_table_uuid();
		if (!requested_id || requested_id == '')
			return;

		const res = await Grape.fetches.getJSON('/api/record', {
			schema: 'fields',
			table: 'v_fields_table',
			limit: 10,
			filter: [{
				field: 'fields_table_uuid', op: '=', value: requested_id
			}]
		});

		if (res.status == 'OK')
		{
			if (res.records.length > 0)
				this.set(res.records[0]);
			else
				Grape.alerts.alert({type: 'warning', 'message': 'No table found with specified uuid'});
		}
		else
			Grape.alerts.alert({type: 'error', message: res.message || 'Fail' });
	}

	async save()
	{
		if (!this.schema())
			await Grape.alerts.alert({type: 'warning', message: 'Schema name cannot be empty!'});
		else if (!this.table())
			await Grape.alerts.alert({type: 'warning', message: 'Table name cannot be empty!'});

		else
		{
			let payload = this.serialize();

			if (typeof payload.options === 'string')
			{
				try {
					payload.options = JSON.parse(payload.options);
				} catch (err) {
					return {status: 'ERROR', code: 'Invalid Data', message:'Options is not valid JSON'};
				}
			}

			return Grape.fetches.postJSON('/api/custom-fields/table/upsert', payload);
		}
	}

	async delete()
	{
		return Grape.fetches.deleteJSON('/api/custom-fields/table/delete', {fields_table_uuid: this.fields_table_uuid});
	}

	async move_up() {
		// TODO
	}

	async move_down() {

	}

	async move_field_up() {

	}

	async move_field_down() {

	}

	async set(input)
	{
		this.base_object = input;

		if (!input) return;

		this.fields_table_uuid(input.fields_table_uuid ?? input.uuid);
		this.schema(input.schema ?? input_defaults.schema);
		this.table(input.table ?? input_defaults.table);
		this.display_name(input.display_name ?? input.table_display_name ?? input_defaults.display_name);
		this.idx(input.table_idx ?? input_defaults.idx);
		this.options(JSON.stringify(input.options) ?? JSON.stringify(input_defaults.options));

		let fields = input.fields ?? input_defaults.fields;
		if (fields.length > 0)
		{	
			// Add missing data
			fields.forEach(f => {
				f.fields_table_uuid = input.fields_table_uuid ?? input.uuid;
				f.schema = input.schema ?? input_defaults.schema;
				f.table = input.table ?? input_defaults.table;
			});
			
			fields = fields.sort((a, b) => a.idx - b.idx);
		}

		fields = fields.map(f => new FieldModel(f));
		this.fields(fields);
	}


	serialize()
	{
		return {
			fields_table_uuid: this.fields_table_uuid(),
			schema: this.schema(),
			table: this.table(),
			display_name: this.display_name(),
			idx: this.idx(),
			options: this.options()
		};
	}

	/**
	*	method to apply pending changes from the ko-field-table-builder component
	*	This centralises all the API calls

	*	Usage in pages:
	*	1. Add the component:
	*	<!-- ko component: {name: 'ko-field-table-builder', params: {on_changes: function(changes) { $root.field_table_changes(changes); }}} --><!-- /ko -->

	*	2. In page's viewModel:
	*	this.field_table_changes = ko.observable(null);

	*	3. In save method:
	*	if (this.field_table_changes()) {
	*		let FieldsTableModel = Grape.dataModel.get('fields.FieldsTable');
	*		let result = await FieldsTableModel.apply_pending_changes(this.field_table_changes());
	*		if (result.status === 'OK') {
	*			this.field_table_changes(null); // Clear changes
	*		}
	*	}
	*/
	static async apply_pending_changes(changes)
	{
		try {
			// Create a mapping of temporary table UUIDs to real UUIDs
			const tempToRealTableUuid = new Map();

			// Create new tables
			for (const table of changes.tables_to_create) {
				const tableForApi = { ...table };
				delete tableForApi.fields_table_uuid; // Remove temp UUID

				const result = await Grape.fetches.postJSON('/api/custom-fields/table/upsert', tableForApi);

				let realUuid = null;
				if (result.status === 'OK') {
					realUuid = result.fields_table_uuid || result.record?.fields_table_uuid;
					if (realUuid) {
						tempToRealTableUuid.set(table.fields_table_uuid, realUuid);
					} else {
						console.error('No UUID found in table creation response:', result);
					}
				} else {
					Grape.alerts.alert({type: 'error', message: 'Table creation failed: ' + result.message});
					console.error('Table creation failed:', result);
				}
			}

			// Update tables
			for (const table of changes.tables_to_update) {
				await Grape.fetches.postJSON('/api/custom-fields/table/upsert', table);
			}

			// Update fields that reference newly created tables
			for (const field of changes.fields_to_create) {
				// Create a copy of the field
				const fieldToCreate = { ...field };

				// If this field references a temporary table UUID, update it to the real UUID
				if (tempToRealTableUuid.has(fieldToCreate.fields_table_uuid)) {
					const oldUuid = fieldToCreate.fields_table_uuid;
					fieldToCreate.fields_table_uuid = tempToRealTableUuid.get(fieldToCreate.fields_table_uuid);
				}

				await Grape.fetches.postJSON('/api/custom-fields/field/upsert', fieldToCreate);
			}
			
			// Update fields
			for (const field of changes.fields_to_update) {
				await Grape.fetches.postJSON('/api/custom-fields/field/upsert', field);
			}

			// Delete tables
			for (const table of changes.tables_to_delete) {
				await Grape.fetches.deleteJSON('/api/custom-fields/table/delete', { fields_table_uuid: table.fields_table_uuid });
			}

			// Delete fields
			for (const field of changes.fields_to_delete) {
				await Grape.fetches.deleteJSON('/api/custom-fields/field/delete', { field_uuid: field.field_uuid });
			}

			// Apply table moves
			for (const move of changes.table_moves) {
				await Grape.fetches.postJSON('/api/custom-fields/table/upsert', move.table_one);
				await Grape.fetches.postJSON('/api/custom-fields/table/upsert', move.table_two);
			}

			// Apply field moves
			for (const move of changes.field_moves) {
				await Grape.fetches.postJSON('/api/custom-fields/field/upsert', move.field_one);
				await Grape.fetches.postJSON('/api/custom-fields/field/upsert', move.field_two);
			}

			return { status: 'OK', message: 'Changes saved successfully' };
		} catch (error) {
			console.error('Error applying field table changes:', error);
			return { status: 'ERROR', message: 'Error saving changes: ' + error.message };
		}
	}
}

export default FieldsTableModel;
